import React, { useCallback, useState, type JSX, type SetStateAction } from "react";
import ChevronRight from "../../img/chevron.svg?react";
import { Pagination, type PaginationProps } from "@gravity-ui/uikit";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "../../utils/useMediaQuery";

export interface Column<T> {
  header: string | React.ReactNode;
  accessor: keyof T | ((row: T) => React.ReactNode);
  width?: string;
  align?: "left" | "center" | "right";
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  selectedRows?: T[];
  setSelectedRows?: React.Dispatch<SetStateAction<T[]>>;
  itemsPerPage?: number;
  totalPages: number;
  pagination: { page: number; itemsPerPage: number };
  setPagination: React.Dispatch<SetStateAction<{ page: number; itemsPerPage: number }>>;
  onRowAction?: (row: T) => void;
  onRowSelect?: (selectedRows: T[]) => void;
  wrapperClassName?: string;
  tableClassName?: string;
  navClassName?: string;
  totalRecords: number;
  isLoading?: boolean;
  rowKey?: keyof T;
}

export function Table<T extends Record<string, any>>({
  columns,
  data,
  selectedRows = [],
  setSelectedRows,
  onRowSelect,
  pagination,
  setPagination,
  totalPages,
  totalRecords,
  itemsPerPage = 10,
  onRowAction,
  wrapperClassName = "",
  tableClassName = "",
  navClassName = "",
  isLoading,
  rowKey = "id",
}: TableProps<T>): JSX.Element {
  const { t } = useTranslation();
  const mediaQuery = useMediaQuery("(max-width:768px)");

  const startIdx = (pagination.page - 1) * pagination.itemsPerPage;
  const endIdx = Math.min(startIdx + pagination.itemsPerPage, totalRecords);

  const handleUpdate: PaginationProps["onUpdate"] = useCallback(
    (page: number, itemsPerPage: number) => {
      setPagination({ page, itemsPerPage });
    },
    [setPagination]
  );

  const isRowSelected = useCallback(
    (row: T) => selectedRows?.some((r) => r?.[rowKey] === row?.[rowKey]),
    [selectedRows, rowKey]
  );

  const toggleRowSelection = (row: T) => {
    if (!setSelectedRows) return;

    const exists = selectedRows?.some((r) => r?.[rowKey] === row?.[rowKey]);
    const updated = exists ? selectedRows.filter((r) => r?.[rowKey] !== row?.[rowKey]) : [...selectedRows, row];

    setSelectedRows(updated);
    onRowSelect?.(updated);
  };

  const renderCell = useCallback((row: T, col: Column<T>) => {
    const cell = typeof col.accessor === "function" ? col.accessor(row) : (row[col.accessor] as React.ReactNode);
    return (
      <td
        key={typeof col.accessor === "string" ? col.accessor : col.header?.toString() ?? ""}
        className={`
          px-4 py-3 whitespace-nowrap text-sm text-gray-800
          ${col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left"}
        `}
      >
        {cell}
      </td>
    );
  }, []);

  return (
    <div
      className={`${wrapperClassName} bg-white rounded-xl overflow-hidden shadow-sm flex flex-col justify-between pb-3`}
    >
      <div className="overflow-x-auto">
        <table className={`${tableClassName} min-w-full divide-y divide-gray-200`}>
          <thead className="bg-white">
            <tr>
              {setSelectedRows && (
                <th className="px-4 py-2 w-8 text-left">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && data.every((row) => isRowSelected(row))}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate =
                          data.some((row) => isRowSelected(row)) && !data.every((row) => isRowSelected(row));
                      }
                    }}
                    onChange={() => {
                      if (!setSelectedRows) return;

                      const allSelected = data.every((row) => isRowSelected(row));
                      const updated = allSelected
                        ? selectedRows.filter((r) => !data.some((row) => r?.[rowKey] === row?.[rowKey]))
                        : [...selectedRows, ...data.filter((row) => !isRowSelected(row))];

                      setSelectedRows(updated);
                      onRowSelect?.(updated);
                    }}
                    className="w-5 h-5 custom-checkbox"
                  />
                </th>
              )}

              {columns.map((col, index) => (
                <th
                  key={typeof col.accessor === "string" ? col.accessor : `col-${index}`}
                  className={`
                    px-4 py-2.5 text-base font-semibold text-gray-700 uppercase
                    ${col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left"}
                    ${col.width ?? ""}
                  `}
                >
                  {col.header}
                </th>
              ))}
              {onRowAction && (
                <th className="px-4 py-2 w-12 text-right text-xs font-semibold text-gray-700 uppercase">&nbsp;</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length + (onRowAction ? 1 : 0) + (setSelectedRows ? 1 : 0)}
                  className="py-12 h-[300px]"
                >
                  <div className="flex justify-center items-center w-full h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr key={row[rowKey] ?? i} className="hover:bg-gray-50">
                  {setSelectedRows && (
                    <td className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={isRowSelected(row)}
                        onChange={() => toggleRowSelection(row)}
                        className="w-5 h-5 custom-checkbox"
                      />
                    </td>
                  )}
                  {columns.map((col) => renderCell(row, col))}
                  {onRowAction && (
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button onClick={() => onRowAction(row)} className="text-orange-600 hover:text-orange-800">
                        <ChevronRight className="inline-block -rotate-90 h-5 w-5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={`${navClassName} flex items-center justify-between px-4 mt-3 max-lg:flex-wrap max-md:gap-3`}>
        <div className="text-sm text-gray-600">
          {t("showing")} <span className="font-medium">{totalRecords !== 0 ? startIdx + 1 : 0}</span>–
          <span className="font-medium">{endIdx}</span> {t("of")} <span className="font-medium">{totalRecords}</span>
        </div>

        <Pagination
          className={`self-end ${mediaQuery && "flex-wrap gap-y-2"}`}
          page={pagination.page}
          pageSize={pagination.itemsPerPage}
          total={totalPages * pagination.itemsPerPage}
          showInput
          onUpdate={handleUpdate}
          pageSizeOptions={[10, 20, 30]}
          size="m"
        />
      </div>
    </div>
  );
}
